import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { LanguageProvider } from "./context/LanguageContext";
import { trackPageView } from "./utils/analytics";
import { ACTIVE_ROUTE_LANGS } from "./i18nConfig";
import Home from "./pages/Home";
import MergePDF from "./pages/tools/MergePDF";
import CompressPDF from "./pages/tools/CompressPDF";
import SplitPDF from "./pages/tools/SplitPDF";
import PDFtoWord from "./pages/tools/PDFtoWord";
import WordtoPDF from "./pages/tools/WordtoPDF";
import ExtractText from "./pages/tools/ExtractText";
import PDFPreview from "./pages/tools/PDFPreview";
import PDFtoExcel from "./pages/tools/PDFtoExcel";
import PDFtoImage from "./pages/tools/PDFtoImage";
import RotatePDF from "./pages/tools/RotatePDF";
import DeletePages from "./pages/tools/DeletePages";
import ProtectPDF from "./pages/tools/ProtectPDF";
// Excel tools
import ExcelToPDF from "./pages/tools/ExcelToPDF";
import ExcelToCSV from "./pages/tools/ExcelToCSV";
import CSVToExcel from "./pages/tools/CSVToExcel";
// Static & category pages
import About from "./pages/About";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Disclaimer from "./pages/Disclaimer";
import PdfTools from "./pages/PdfTools";
import ExcelTools from "./pages/ExcelTools";
import ComingSoonCategory from "./pages/ComingSoonCategory";
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  return null;
}

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return null;
}

// Tool route definitions — reused for each language prefix
const TOOL_ROUTES = [
  { path: "merge-pdf",          element: <MergePDF /> },
  { path: "compress-pdf",       element: <CompressPDF /> },
  { path: "split-pdf",          element: <SplitPDF /> },
  { path: "pdf-to-word",        element: <PDFtoWord /> },
  { path: "word-to-pdf",        element: <WordtoPDF /> },
  { path: "extract-text",       element: <ExtractText /> },
  { path: "pdf-preview",        element: <PDFPreview /> },
  { path: "pdf-to-excel",       element: <PDFtoExcel /> },
  { path: "pdf-to-image",       element: <PDFtoImage /> },
  { path: "rotate-pdf",         element: <RotatePDF /> },
  { path: "delete-pdf-pages",   element: <DeletePages /> },
  { path: "protect-pdf",        element: <ProtectPDF /> },
  { path: "excel-to-pdf",       element: <ExcelToPDF /> },
  { path: "excel-to-csv",       element: <ExcelToCSV /> },
  { path: "csv-to-excel",       element: <CSVToExcel /> },
];

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AnalyticsTracker />
        <ScrollToTop />
        <Routes>
          {/* ====== EN routes (default — no prefix) ====== */}
          <Route path="/" element={<Home />} />

          {/* Primary PDF tool routes */}
          <Route path="/merge-pdf"        element={<MergePDF />} />
          <Route path="/compress-pdf"     element={<CompressPDF />} />
          <Route path="/split-pdf"        element={<SplitPDF />} />
          <Route path="/pdf-to-word"      element={<PDFtoWord />} />
          <Route path="/word-to-pdf"      element={<WordtoPDF />} />
          <Route path="/extract-text"     element={<ExtractText />} />
          <Route path="/pdf-preview"      element={<PDFPreview />} />
          <Route path="/pdf-to-excel"     element={<PDFtoExcel />} />
          <Route path="/pdf-to-image"     element={<PDFtoImage />} />
          <Route path="/rotate-pdf"       element={<RotatePDF />} />
          <Route path="/delete-pdf-pages" element={<DeletePages />} />
          <Route path="/protect-pdf"      element={<ProtectPDF />} />

          {/* EN long-tail SEO routes */}
          <Route path="/merge-pdf-online-free"               element={<MergePDF />} />
          <Route path="/compress-pdf-without-losing-quality" element={<CompressPDF />} />
          <Route path="/split-pdf-online"                    element={<SplitPDF />} />
          <Route path="/convert-pdf-to-word-online"          element={<PDFtoWord />} />
          <Route path="/word-to-pdf-converter"               element={<WordtoPDF />} />
          <Route path="/pdf-to-jpg-converter"                element={<PDFtoImage />} />
          <Route path="/extract-text-from-pdf-free"          element={<ExtractText />} />
          <Route path="/rotate-pdf-pages-online"             element={<RotatePDF />} />
          <Route path="/remove-pages-from-pdf"               element={<DeletePages />} />
          <Route path="/add-password-to-pdf"                 element={<ProtectPDF />} />

          {/* EN Excel tool routes */}
          <Route path="/excel-to-pdf"           element={<ExcelToPDF />} />
          <Route path="/excel-to-csv"           element={<ExcelToCSV />} />
          <Route path="/csv-to-excel"           element={<CSVToExcel />} />
          <Route path="/excel-to-pdf-online"    element={<ExcelToPDF />} />
          <Route path="/convert-excel-to-csv"   element={<ExcelToCSV />} />
          <Route path="/csv-to-excel-converter" element={<CSVToExcel />} />

          {/* EN category pages */}
          <Route path="/pdf-tools"    element={<PdfTools />} />
          <Route path="/excel-tools"  element={<ExcelTools />} />
          <Route path="/word-tools"   element={<ComingSoonCategory category="word" />} />
          <Route path="/image-tools"  element={<ComingSoonCategory category="image" />} />

          {/* Blog — lazy loaded to reduce main bundle */}
          <Route path="/blog"       element={<Suspense fallback={<div className="min-h-screen" />}><BlogIndex /></Suspense>} />
          <Route path="/blog/:slug" element={<Suspense fallback={<div className="min-h-screen" />}><BlogPost /></Suspense>} />

          {/* EN legal & company pages */}
          <Route path="/about"            element={<About />} />
          <Route path="/contact"          element={<Contact />} />
          <Route path="/privacy-policy"   element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/disclaimer"       element={<Disclaimer />} />

          {/* ====== Localized routes — fr / es / pt / de ====== */}
          {ACTIVE_ROUTE_LANGS.flatMap((lang) => [
            <Route key={`${lang}-home`}         path={`/${lang}`}             element={<Home />} />,
            <Route key={`${lang}-pdf-tools`}    path={`/${lang}/pdf-tools`}   element={<PdfTools />} />,
            <Route key={`${lang}-excel-tools`}  path={`/${lang}/excel-tools`} element={<ExcelTools />} />,
            ...TOOL_ROUTES.map(({ path, element }) => (
              <Route key={`${lang}-${path}`} path={`/${lang}/${path}`} element={element} />
            )),
          ])}
        </Routes>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
